window.AboutPage = function AboutPage({ setActiveTab, openQuoteModal }) {
  const [aboutFaqs, setAboutFaqs] = React.useState([
    {
      question: "Can Novatek design a completely customized test rig for our non-standard component?",
      answer: "Yes. Over 90% of our systems are custom-engineered from the ground up. Our design bureau works directly from your 3D CAD models, test standards, and acceptance criteria to design bespoke fixtures, load actuators, and hydraulic/pneumatic manifolds."
    },
    {
      question: "Can Novatek integrate custom PLC automation, DAQ hardware, and automated test reporting?",
      answer: "Yes. We have in-house electrical and software development teams that program Siemens, Rockwell, Beckhoff, and Mitsubishi PLCs, alongside high-speed 24-bit DAQ hardware and custom GUI software generating automated tamper-proof PDF test certificates."
    },
    {
      question: "Does Novatek handle Factory Acceptance Testing (FAT) and On-Site Commissioning?",
      answer: "Yes. Every machine undergoes full physical FAT validation at our engineering facility prior to dispatch. Our engineers then handle on-site installation, mechanical anchoring, SAT sign-off, and staff training at your plant."
    },
    {
      question: "What safety and industrial compliance standards do your test rigs adhere to?",
      answer: "Our machines are engineered to adhere strictly to international standards including UIC 540/541 and RDSO specifications for railway equipment, DO-160 and ARP4754 for aerospace stands, and ISO 13849-1 / CE Machinery Directives for industrial safety."
    }
  ]);

  const [teamMembers, setTeamMembers] = React.useState([
    {
      name: "R. K. Sharma",
      role: "Managing Director & Chief Mechanical Architect",
      credentials: "M.Tech (Machine Design, IIT Madras) • 28+ Yrs Exp",
      department: "Executive & Mechanical Architecture",
      photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80",
      bio: "Pioneered high-tonnage servo-hydraulic bogie fatigue test frames and automated pneumatic brake valve calibration stands across Indian Railways and export markets."
    },
    {
      name: "Karthik Subramanian",
      role: "Head of Automation & Embedded Systems",
      credentials: "B.E. (Mechatronics) • Siemens Certified Specialist • 16+ Yrs Exp",
      department: "Control Systems & Software",
      photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80",
      bio: "Leads real-time closed-loop motion control architecture, FPGA high-speed DAQ development, and Industry 4.0 MES SCADA integration."
    },
    {
      name: "Pooja Deshmukh",
      role: "Lead Structural & FEA Simulation Engineer",
      credentials: "M.S. (Computational Mechanics) • ANSYS Certified • 12+ Yrs Exp",
      department: "Design Bureau & Simulation",
      photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80",
      bio: "Specializes in multi-axis dynamic stress analysis, harmonic resonance simulation, and custom GD&T fixture engineering for aerospace and automotive rigs."
    },
    {
      name: "Vikramaditya Rao",
      role: "Head of Quality Assurance & Field Commissioning",
      credentials: "B.Tech (Production) • NABL Calibration Lead • 14+ Yrs Exp",
      department: "FAT / SAT & Metrology",
      photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=600&q=80",
      bio: "Oversees rigorous Factory Acceptance Testing, laser alignment verification, sensor recalibration protocols, and on-site SAT plant deployments."
    }
  ]);

  const [expandedFaq, setExpandedFaq] = React.useState(null);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchAboutContent();

    const handleSync = () => fetchAboutContent();
    window.addEventListener('novatek_content_updated', handleSync);
    window.addEventListener('storage', handleSync);

    if (window.SEOHelper) {
      window.SEOHelper.updatePageMeta(
        "About Novatek | Custom Test & Automation Systems",
        "Novatek designs and manufactures custom test systems, automated test benches and industrial automation solutions for critical engineering applications."
      );
      window.SEOHelper.injectFAQSchema(aboutFaqs);
    }

    return () => {
      window.removeEventListener('novatek_content_updated', handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, []);

  const [aboutPage, setAboutPage] = React.useState({
    hero: {
      bgImage: "/api/storage/banners/about_hq_novatek.webp",
      eyebrow: "ABOUT US",
      titleWhite: "Engineering Innovation.",
      titleBlueLine1: "Building a Smarter",
      titleBlueLine2: "Tomorrow.",
      description: "Novatek is a leading engineering solutions company delivering innovative, high-quality and reliable solutions to industries worldwide. With a passion for technology and a commitment to excellence, we design and manufacture solutions that drive progress and performance."
    },
    pillars: {
      title: "Our Core Pillars",
      subtitle: "Every solution engineered by NOVATEK is built upon four core principles:",
      items: [
        { icon: "architecture", title: "Precision Engineering", description: "Design Without Compromise, From concept to commissioning, every system is engineered using advanced mechanical design, simulation, tolerance analysis, and manufacturing best practices to deliver consistent performance throughout its lifecycle." },
        { icon: "memory", title: "Intelligent Automation", description: "Machines That Think, We develop intelligent automation platforms integrating PLCs, motion control, robotics, vision systems, SCADA, Industrial IoT, and AI-ready architectures for data-driven manufacturing." },
        { icon: "verified", title: "Reliability & Safety", description: "Built for Mission-Critical Operations, Our equipment is designed for industries where failure is not an option. Every machine emphasizes operator safety, process reliability, maintainability, and long service life." },
        { icon: "sync", title: "Innovation Partnership", description: "Beyond Machine Building, We collaborate closely with customers from concept development through lifecycle support, transforming engineering challenges into scalable industrial solutions." }
      ]
    }
  });

  const fetchAboutContent = async () => {
    try {
      const res = await window.apiClient.get('content.php');
      if (res.success && res.data) {
        if (res.data.about_faqs && Array.isArray(res.data.about_faqs) && res.data.about_faqs.length > 0) {
          // Normalize {q, a} or {question, answer}
          const mappedFaqs = res.data.about_faqs.map(f => ({
            question: f.question || f.q,
            answer: f.answer || f.a
          }));
          setAboutFaqs(mappedFaqs);
        }
        if (res.data.about_team && Array.isArray(res.data.about_team) && res.data.about_team.length > 0) {
          setTeamMembers(res.data.about_team);
        }
        if (res.data.about_page && res.data.about_page.hero && res.data.about_page.pillars) {
          setAboutPage(res.data.about_page);
        }
      }
    } catch (e) {
      console.warn('AboutPage CMS sync warning:', e);
    }
  };

  const capabilities = [
    {
      icon: "speed",
      title: "Test Engineering",
      subtitle: "Custom test rigs, validation benches & EOL machines.",
      bullets: ["Dynamic Fatigue Validation", "Pneumatic/Hydraulic Burst & Proof", "Automated EOL Quality Inspection"]
    },
    {
      icon: "precision_manufacturing",
      title: "Industrial Automation",
      subtitle: "Robotic cells, PLC & motion control.",
      bullets: ["6-Axis Articulated Robots", "Multi-Station Rotary Tables", "Cat 4 PLe Safety Interlocks"]
    },
    {
      icon: "settings",
      title: "Mechanical Engineering",
      subtitle: "Heavy structures, fixtures & mechanisms.",
      bullets: ["3D Parametric CAD (SolidWorks/NX)", "FEA Stress & Vibration Simulation", "Zero-Backlash Clamping Fixtures"]
    },
    {
      icon: "sensors",
      title: "Instrumentation & DAQ",
      subtitle: "Sensors, measurement & high-speed DAQ.",
      bullets: ["Piezoelectric Load Transducers", "LVDT & Laser Optical Gauges", "100 kHz High-Speed Sampling"]
    },
    {
      icon: "water_drop",
      title: "Pneumatic & Hydraulic Systems",
      subtitle: "Fluid power & micro-leak detection.",
      bullets: ["Up to 350 Bar Hydraulic Loops", "0.1 Pa Differential Leak Decay", "Skydrol & Oil Conditioning"]
    },
    {
      icon: "bolt",
      title: "Electrical & Control",
      subtitle: "Control panels & safety distribution.",
      bullets: ["IP54 / IP65 Electrical Panels", "Shielded Zero-Noise Sensor Wiring", "Safety Relay & Emergency Interlocks"]
    },
    {
      icon: "terminal",
      title: "Software & Traceability",
      subtitle: "Custom test software & digital reporting.",
      bullets: ["Automated Test Sequence Execution", "SQL Database Component Logging", "Instant PDF Certificate Generation"]
    },
    {
      icon: "hub",
      title: "IoT & Industry 4.0",
      subtitle: "Factory connectivity & telemetry.",
      bullets: ["OPC-UA / MQTT Cloud Integration", "Live OEE Dashboard Telemetry", "Predictive Vibration Monitoring"]
    }
  ];

  const facilitySections = [
    {
      title: "Design Bureau & Simulation Lab",
      desc: "Equipped with advanced 3D CAD parametric modeling workstations, Finite Element Analysis (FEA) solvers, and CFD simulation tools.",
      icon: "architecture"
    },
    {
      title: "Precision CNC & Fabrication Bay",
      desc: "Heavy-duty CNC machining centers, precision surface grinding, structural steel welding, and certified weld inspection.",
      icon: "construction"
    },
    {
      title: "Clean Hydraulic & Mechanical Assembly Bay",
      desc: "Dedicated dust-controlled assembly bays for high-pressure hydraulic manifold piping, valve assembly, and mechanical integration.",
      icon: "clean_hands"
    },
    {
      title: "Electrical & Control Panel Building Section",
      desc: "In-house panel wiring, Rittal enclosures, Siemens S7-1500 / Beckhoff PLC racks, and strict CE/IP54 wiring standards.",
      icon: "electric_bolt"
    },
    {
      title: "Full-Scale Machine Testing & FAT Floor",
      desc: "Heavy reinforced test floor with multi-ton crane access, pneumatic supply grids, 350 Bar hydraulic power units, and high-speed DAQ.",
      icon: "flaky"
    },
    {
      title: "Quality & NABL-Traceable Metrology Lab",
      desc: "Calibrated precision master gauges, deadweight testers, optical surface roughness meters, and electronic micrometers.",
      icon: "verified"
    }
  ];

  return (
    <div className="space-y-16 py-12 bg-slate-50">

      {/* 1. HERO SECTION */}
      <section className="relative w-full h-[450px] lg:h-[500px] flex items-center bg-[#030816] overflow-hidden -mt-12 mb-16">
        {/* Right side image (background) */}
        <div className="absolute right-0 top-0 w-full lg:w-[65%] h-full">
          <img
            src={aboutPage.hero.bgImage}
            alt="Novatek Facility"
            className="w-full h-full object-cover object-center opacity-40 lg:opacity-100"
          />
        </div>

        {/* Left side slanted dark panel */}
        <div
          className="absolute top-0 left-0 h-full w-full lg:w-[68%] bg-[#030816] z-10 lg:[clip-path:polygon(0_0,100%_0,80%_100%,0%_100%)] opacity-95 lg:opacity-100"
        ></div>

        {/* Content Container */}
        <div className="relative z-20 w-full px-6 lg:px-16 flex">
          <div className="w-full lg:w-[60%]">
            <div className="space-y-0">
              <span className="font-mono text-[11px] sm:text-[12px] font-bold text-yellow-500 tracking-[0.2em] uppercase block mb-4">
                {aboutPage.hero.eyebrow}
              </span>
              <h1 className="font-sans text-5xl sm:text-6xl lg:text-[64px] font-extrabold tracking-tight leading-[1.05]">
                <span className="text-white block mb-0.5">{aboutPage.hero.titleWhite}</span>
                <span className="text-blue-500 block mb-0.5">{aboutPage.hero.titleBlueLine1}</span>
                <span className="text-blue-500 block">{aboutPage.hero.titleBlueLine2}</span>
              </h1>
              <div className="w-12 h-[3px] bg-blue-500 mt-6 mb-6"></div>
              {aboutPage.hero.description && (
                <p className="font-sans text-slate-300 text-sm sm:text-base leading-relaxed max-w-lg">
                  {aboutPage.hero.description}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 2. OUR CORE PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-12">
          <h2 className="font-sans text-3xl sm:text-4xl font-bold text-[#0f172a] tracking-tight">
            {aboutPage.pillars.title}
          </h2>
          <p className="font-sans text-slate-500 text-sm sm:text-base">
            {aboutPage.pillars.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {aboutPage.pillars.items.map((item, idx) => (
            <div key={idx} className="bg-white rounded-[20px] border border-slate-100 p-8 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] hover:shadow-lg transition-shadow">
              <div className="h-10 w-10 rounded-xl bg-orange-50 text-[#f97316] flex items-center justify-center mb-6">
                <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
              </div>
              <h3 className="font-sans text-base font-bold text-[#0f172a] mb-3">{item.title}</h3>
              <p className="font-sans text-[13px] text-slate-500 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 3. CAPABILITIES GRID */}
      <section className="bg-slate-100/70 py-16 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">

          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
              Comprehensive Capabilities
            </span>
            <h2 className="font-sans text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              8 Integrated Engineering Pillars
            </h2>
            <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
            <p className="font-sans text-slate-600 text-base">
              Our multidisciplinary engineering team delivers end-to-end solutions across mechanics, electronics, fluid power, and real-time software.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((cap, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all space-y-3 flex flex-col justify-between"
              >
                <div className="h-10 w-10 rounded-xl bg-orange-100 text-secondary flex items-center justify-center">
                  <span className="material-symbols-outlined text-xl">{cap.icon}</span>
                </div>
                <h3 className="font-sans text-base font-bold text-slate-900">{cap.title}</h3>
                <p className="font-sans text-xs text-slate-500">{cap.subtitle}</p>
                <ul className="space-y-1.5 pt-2 border-t border-slate-200 text-xs font-sans text-slate-700">
                  {cap.bullets.map((b, i) => (
                    <li key={i} className="flex items-center gap-1.5">
                      <span className="text-secondary font-bold font-mono">▸</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 4. ENGINEERING & MANUFACTURING FACILITY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
            Infrastructure & Machinery
          </span>
          <h2 className="font-sans text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Our Engineering & Manufacturing Facility
          </h2>
          <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
          <p className="font-sans text-slate-600 text-base">
            Located in the industrial corridor of Hosur / Bengaluru, our integrated facility houses complete in-house capabilities from design to calibration.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {facilitySections.map((fac, idx) => (
            <div key={idx} className="bg-white p-7 rounded-2xl border border-slate-200 shadow-sm space-y-3">
              <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-800 flex items-center justify-center">
                <span className="material-symbols-outlined text-xl">{fac.icon}</span>
              </div>
              <h3 className="font-sans text-lg font-bold text-slate-900">{fac.title}</h3>
              <p className="font-sans text-xs sm:text-sm text-slate-600 leading-relaxed">{fac.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. OUR LEADERSHIP & ENGINEERING TEAM SHOWCASE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
            Domain Specialists & Leadership
          </span>
          <h2 className="font-sans text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Our Leadership & Engineering Team
          </h2>
          <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
          <p className="font-sans text-slate-600 text-base">
            Led by senior mechanical architects, control systems developers, and metrology specialists with decades of proven industry tenure.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {teamMembers.map((member, idx) => {
            const photoUrl = window.resolveMediaUrl(member.photo, 'person');
            return (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="h-64 bg-slate-900 overflow-hidden relative group">
                    <img
                      src={photoUrl}
                      alt={member.name}
                      onError={(e) => window.handleImageError(e, 'person')}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <span className="absolute bottom-3 left-3 bg-slate-900/85 backdrop-blur-xs text-secondary text-[11px] font-mono font-bold px-2.5 py-1 rounded border border-orange-500/30">
                      {member.department || "Engineering"}
                    </span>
                  </div>

                  <div className="p-5 space-y-2">
                    <h3 className="font-sans text-lg font-bold text-slate-900 leading-snug">
                      {member.name}
                    </h3>
                    <span className="text-xs font-sans font-semibold text-secondary block">
                      {member.role}
                    </span>
                    {member.credentials && (
                      <span className="text-[11px] font-mono text-slate-500 block">
                        {member.credentials}
                      </span>
                    )}
                    {member.bio && (
                      <p className="text-xs font-sans text-slate-600 leading-relaxed pt-2 border-t border-slate-100">
                        {member.bio}
                      </p>
                    )}
                  </div>
                </div>

                <div className="px-5 pb-5 pt-0">
                  <div className="bg-slate-50 py-2 px-3 rounded-xl border border-slate-100 flex items-center justify-between text-[11px] font-mono text-slate-600">
                    <span>Novatek Lead</span>
                    <span className="material-symbols-outlined text-secondary text-sm">verified</span>
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      </section>

      {/* 6. INTERACTIVE TECHNICAL FAQS */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center space-y-3">
          <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
            Frequently Asked Questions
          </span>
          <h2 className="font-sans text-3xl font-bold text-slate-900">
            Frequently Asked Engineering Questions
          </h2>
          <div className="h-1 w-16 bg-secondary mx-auto rounded-full"></div>
        </div>

        <div className="space-y-3">
          {aboutFaqs.map((faq, idx) => {
            const isOpen = expandedFaq === idx;
            return (
              <div
                key={idx}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm transition-all"
              >
                <button
                  onClick={() => setExpandedFaq(isOpen ? null : idx)}
                  className="w-full p-5 text-left flex justify-between items-center gap-4 font-sans font-bold text-sm sm:text-base text-slate-900 hover:text-secondary transition-colors"
                >
                  <span>{faq.question}</span>
                  <span className="material-symbols-outlined text-secondary shrink-0">
                    {isOpen ? 'remove' : 'add'}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 font-sans text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3 animate-fadeIn">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 7. CALL TO ACTION */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-900 text-white rounded-2xl p-8 sm:p-12 text-center space-y-4 shadow-xl">
          <h3 className="font-sans text-2xl sm:text-3xl font-bold">
            Visit Our Facility or Request an Engineering Consultation
          </h3>
          <p className="font-sans text-slate-300 text-xs sm:text-base max-w-xl mx-auto">
            Schedule a technical discussion with our design engineers to review machine layout, cycle times, and budget feasibility.
          </p>
          <div className="pt-2">
            <button
              onClick={openQuoteModal}
              className="bg-secondary hover:bg-orange-600 text-white font-sans font-bold text-sm px-8 py-3.5 rounded-xl shadow-lg transition-all active:scale-95 inline-flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-lg">engineering</span>
              <span>Request Engineering Consultation</span>
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};
